import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Control, useWatch } from 'react-hook-form';
import { Alert, Snackbar, Stack, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormattedMessage, useIntl } from 'react-intl';

import { GET_AMCR_FIND_PREVIEWS_QUERY } from '../../gql';
import {
  AmcrFindPreviewResult,
  AmcrPasCreateDialog,
  AmcrPasFile,
  AmcrPasPhotoDialog,
  getAmcrProblemMessage,
} from '../AmcrPasDialog';
import { Button, IconButton } from '../design-system';

interface AmcrPasControlProps {
  findId: string;
  /** Set only for PAS records created from Acolyte. */
  amcrStatus?: string | null;
  /** Files already stored in the PAS record. */
  pasFiles: AmcrPasFile[];
  control: Control<any>;
  sites: { id: string; amcrProjectIds?: string[] | null }[];
  isDirty: boolean;
  onCreated: (amcrId: string) => void;
}

const WATCHED_FIELDS = ['amcrId', 'siteId'] as const;
const reasonListStyles = { m: 0, pl: 2 };
const buttonStyles = { flexShrink: 0 };
const TOAST_DURATION_MS = 5000;

/**
 * Offers "Create in PAS" until Acolyte has created the PAS record, and
 * "Add PAS photos" afterwards. The two actions are never offered together.
 */
const AmcrPasControl = React.memo(
  ({
    findId,
    amcrStatus,
    pasFiles,
    control,
    sites,
    isDirty,
    onCreated,
  }: AmcrPasControlProps) => {
    const intl = useIntl();
    const [openDialog, setOpenDialog] = useState<'create' | 'photos' | null>(
      null
    );
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [amcrId, siteId] = useWatch({ control, name: WATCHED_FIELDS });
    const findIds = useMemo(() => [findId], [findId]);

    const site = sites.find((option) => option.id === siteId);
    const isCreatedInPas = Boolean(amcrStatus);
    const canCreate =
      !isCreatedInPas &&
      !amcrId?.trim() &&
      Boolean(site?.amcrProjectIds?.length);

    const { data, error } = useQuery(GET_AMCR_FIND_PREVIEWS_QUERY, {
      variables: { findIds },
      fetchPolicy: 'cache-and-network',
      skip: !canCreate,
    });
    const preview: AmcrFindPreviewResult | undefined =
      data?.getAmcrFindPreviews?.[0];

    const reasons = [
      ...(isDirty
        ? [
            intl.formatMessage({
              id: 'amcrPas.problem.unsaved',
              defaultMessage: 'Save your changes first.',
            }),
          ]
        : []),
      ...(error && !preview
        ? [
            intl.formatMessage({
              id: 'amcrPas.problem.checkFailed',
              defaultMessage:
                'The PAS requirements could not be checked. Try reloading the page.',
            }),
          ]
        : []),
      ...(preview?.errors || []).map((detail) =>
        getAmcrProblemMessage(intl, detail)
      ),
    ];

    const handleOpenCreate = () => {
      setOpenDialog('create');
    };

    const handleOpenPhotos = () => {
      setOpenDialog('photos');
    };

    const handleCloseDialog = () => {
      setOpenDialog(null);
    };

    const handleCreated = (createdId: string) => {
      setToastMessage(
        intl.formatMessage(
          {
            id: 'amcrPas.create.success',
            defaultMessage: 'The find was created in PAS as {amcrId}.',
          },
          { amcrId: createdId }
        )
      );
      onCreated(createdId);
    };

    const handlePhotosUploaded = (count: number) => {
      setToastMessage(
        intl.formatMessage(
          {
            id: 'amcrPas.photos.success',
            defaultMessage:
              '{count, plural, one {# photo was} other {# photos were}} added to PAS.',
          },
          { count }
        )
      );
    };

    const handleToastClose = () => {
      setToastMessage(null);
    };

    const reasonList = (
      <Stack component="ul" sx={reasonListStyles}>
        {reasons.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </Stack>
    );

    // The toast outlives the dialogs and the button that opened them.
    return (
      <>
        {isCreatedInPas && (
          <Button onClick={handleOpenPhotos} sx={buttonStyles}>
            <FormattedMessage
              id="amcrPas.photos.button"
              defaultMessage="Add PAS photos"
            />
          </Button>
        )}
        {canCreate && (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Button
              disabled={!preview || reasons.length > 0}
              onClick={handleOpenCreate}
              sx={buttonStyles}
            >
              <FormattedMessage
                id="amcrPas.create.button"
                defaultMessage="Create in PAS"
              />
            </Button>
            {reasons.length > 0 && (
              <Tooltip title={reasonList}>
                <IconButton
                  tone="neutral"
                  density="compact"
                  aria-label={intl.formatMessage({
                    id: 'amcrPas.create.reasons',
                    defaultMessage: 'Why the find cannot be sent to PAS',
                  })}
                >
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
        {openDialog === 'create' && (
          <AmcrPasCreateDialog
            findId={findId}
            onClose={handleCloseDialog}
            onCreated={handleCreated}
          />
        )}
        {openDialog === 'photos' && (
          <AmcrPasPhotoDialog
            findId={findId}
            pasFiles={pasFiles}
            onClose={handleCloseDialog}
            onUploaded={handlePhotosUploaded}
          />
        )}
        <Snackbar
          open={Boolean(toastMessage)}
          autoHideDuration={TOAST_DURATION_MS}
          onClose={handleToastClose}
        >
          <Alert severity="success" variant="filled" onClose={handleToastClose}>
            {toastMessage}
          </Alert>
        </Snackbar>
      </>
    );
  }
);

AmcrPasControl.displayName = 'AmcrPasControl';

export { AmcrPasControl };
